// Mirrors web-shared's SharedForm.styled.tsx `AsideStep` exactly: numbered counter,
// primary-green for the active step, gray for the rest, connecting line, extraLoose spacing.

import styled, {
  css,
  FontWeights,
  resetList,
  spacing,
} from "@rentiohq/web-theme";
import { rem } from "polished";
import React from "react";

export interface StepDef {
  key: string;
  title: string;
}

export interface StepNavigationProps {
  steps: StepDef[];
  activeIndex: number;
  onStepClick: (index: number) => void;
  // Steps before this index can no longer be opened (the settlement is
  // already saved): they show as done but are not clickable, and hovering
  // them shows `lockedHint`.
  lockedBefore?: number;
  lockedHint?: string;
}

const currentColorLine = css`
  background-repeat: repeat-y;
  background-image: linear-gradient(
    to right,
    currentColor 100%,
    currentColor 0
  );
  background-size: 2px 2px;
  background-position: 11px 0;
`;

const List = styled.ol`
  ${resetList};
  counter-reset: section;
`;

const AsideStep = styled.li<{
  isPassed: boolean;
  isCurrent: boolean;
  isLocked: boolean;
}>`
  position: relative;
  width: 100%;
  display: flex;
  font-weight: ${FontWeights.Bold};
  ${currentColorLine}
  &::before {
    width: ${({ theme }) => rem(24, theme.baseFontSize)};
    text-align: center;
    display: inline-block;
    margin-right: ${spacing()};
    counter-increment: section;
    content: counters(section, ".") " ";
    background-color: ${({ theme }) => theme.getColor("white")};
    vertical-align: top;
    align-self: flex-start;
    flex: 0 0 auto;
  }
  & + & {
    padding-top: ${spacing("extraLoose")};
  }
  ${({ theme, isPassed, isCurrent, isLocked }) => {
    if (isPassed && !isCurrent) {
      return `
        color: ${theme.getColor("gray", 1000)};
        cursor: ${isLocked ? "not-allowed" : "pointer"};
      `;
    }
    if (isPassed && isCurrent) {
      return `
        background-image: linear-gradient(to right, ${theme.getColor(
          "gray",
          1000,
        )} 100%, ${theme.getColor("gray", 1000)} 0);
        color: ${theme.getColor("primary")};
      `;
    }
    return `color: ${theme.getColor("gray", 400)};`;
  }};
`;

export const StepNavigation: React.FC<StepNavigationProps> = ({
  steps,
  activeIndex,
  onStepClick,
  lockedBefore = 0,
  lockedHint,
}) => (
  <List>
    {steps.map((s, i) => {
      const isCurrent = i === activeIndex;
      const isPassed = i <= activeIndex;
      const isLocked = i < lockedBefore && !isCurrent;
      return (
        <AsideStep
          key={s.key}
          isPassed={isPassed}
          isCurrent={isCurrent}
          isLocked={isLocked}
          title={isLocked ? lockedHint : undefined}
          onClick={() => {
            if (isPassed && !isCurrent && !isLocked) onStepClick(i);
          }}
        >
          {s.title}
        </AsideStep>
      );
    })}
  </List>
);
